import React, { useMemo } from "react";

import { type Group } from "@eisox/api/core-api";
import { Breadcrumb, Page, ToggleGroup } from "@eisox/design-system";
import { BinIcon, PencilIcon, PlusIcon } from "@eisox/icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";

import { groupApi } from "~/api";
import {
  AddGroupDialog,
  DeleteGroupDialog,
  findPathToNode,
  flattenGroupsAndHouses,
  Navigation,
  RecursiveNavigation,
  Search,
  UpdateGroupDialog,
} from "~/features/groups";
import { documentTitleHead } from "~/helpers/document-title.helper";
import { m } from "~/paraglide/messages";
import { GroupProvider } from "~/providers/group.provider";

export const Route = createFileRoute("/_authenticated/_no-building/groups/$groupId")({
  head: () => documentTitleHead({ page: m.groups() }),
  component: RouteComponent,
});

function GroupRoute({ groupId, groups }: { groupId: string; groups: Group[] }) {
  const navigate = Route.useNavigate();
  const { pathname } = useLocation();

  const path = useMemo(() => findPathToNode(groups, groupId), [groups, groupId]);

  const searchResults = useMemo(() => flattenGroupsAndHouses(groups), [groups]);

  const handleToggleGroupChange = (value: string) => {
    void navigate({ to: value });
  };

  const sortedGroups = useMemo(() => groups.sort((a, b) => a.name.localeCompare(b.name)), [groups]);

  return (
    <Page.Root>
      <Page.Header>
        <Page.Title>{m.groups()}</Page.Title>
      </Page.Header>
      <Page.Content className="gap-2.5 flex bg-transparent">
        <div className={"w-72.5 gap-2.5 p-5 flex flex-col rounded-[10px] bg-primary-white"}>
          <Search
            searchResults={searchResults}
            onSelect={id => {
              void navigate({ to: "/groups/$groupId/buildings", params: { groupId: id } });
            }}
          />
          <Navigation.Root className={"overflow-y-auto"}>
            {sortedGroups.map(group => (
              <Navigation.Item key={group.id}>
                <Navigation.ItemButton asChild isActive={group.id === groupId}>
                  <Link to={"/groups/$groupId/buildings"} params={{ groupId: group.id }}>
                    {group.name}
                  </Link>
                </Navigation.ItemButton>
                {import.meta.env.VITE_GROUPS_ACTIONS_ENABLED && (
                  <Navigation.ItemActions>
                    <AddGroupDialog parent={group}>
                      <Navigation.ItemAction>
                        <PlusIcon />
                      </Navigation.ItemAction>
                    </AddGroupDialog>
                    <UpdateGroupDialog group={group}>
                      <Navigation.ItemAction>
                        <PencilIcon />
                      </Navigation.ItemAction>
                    </UpdateGroupDialog>
                    {group.id !== groupId && (
                      <DeleteGroupDialog group={group}>
                        <Navigation.ItemAction>
                          <BinIcon />
                        </Navigation.ItemAction>
                      </DeleteGroupDialog>
                    )}
                  </Navigation.ItemActions>
                )}
                {group.descendants.length > 0 && (
                  <Navigation.Sub>
                    <RecursiveNavigation groups={group.descendants} activeId={groupId} />
                  </Navigation.Sub>
                )}
              </Navigation.Item>
            ))}
          </Navigation.Root>
        </div>
        <div className={"gap-2.5 p-5 flex flex-1 flex-col items-start rounded-[10px] bg-primary-white"}>
          {path && (
            <Breadcrumb.Root>
              <Breadcrumb.List>
                {path.map((node, index) =>
                  index !== path.length - 1 ? (
                    <React.Fragment key={node.id}>
                      <Breadcrumb.Item>
                        <Breadcrumb.Link asChild>
                          <Link to={"/groups/$groupId/buildings"} params={{ groupId: node.id }}>
                            {node.name}
                          </Link>
                        </Breadcrumb.Link>
                      </Breadcrumb.Item>
                      <Breadcrumb.Separator />
                    </React.Fragment>
                  ) : (
                    <Breadcrumb.Item key={node.id}>
                      <Breadcrumb.Page className={"font-semibold text-[1.25rem]"}>{node.name}</Breadcrumb.Page>
                    </Breadcrumb.Item>
                  ),
                )}
              </Breadcrumb.List>
            </Breadcrumb.Root>
          )}
          <ToggleGroup.Root value={pathname} onValueChange={handleToggleGroupChange}>
            <ToggleGroup.Item className={"text-base"} value={`/groups/${groupId}/buildings`}>
              {m.buildings()}
            </ToggleGroup.Item>
            <ToggleGroup.Item className={"text-base"} value={`/groups/${groupId}/users`}>
              {m.users()}
            </ToggleGroup.Item>
          </ToggleGroup.Root>
          <Outlet />
        </div>
      </Page.Content>
    </Page.Root>
  );
}

function RouteComponent() {
  const { groupId } = Route.useParams();
  const { data: groups } = useSuspenseQuery(groupApi.queries.all);

  return (
    <GroupProvider groupId={groupId} groups={groups}>
      <GroupRoute groupId={groupId} groups={groups} />
    </GroupProvider>
  );
}
